class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    // Etsy-style empty state (recent + popular searches)
    this.recentSearchesKey = 'etsy-recent-searches';
    this.maxRecentSearches = 5;
    this.emptyPanel = this.querySelector('[data-predictive-search-empty]');
    this.recentWrapper = this.querySelector('[data-recent-searches]');
    this.recentList = this.querySelector('[data-recent-searches-list]');
    this.recentItemTemplate = this.querySelector('[data-recent-item-template]');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onInnerClick.bind(this));
  }

  onInnerClick(event) {
    const removeButton = event.target.closest('[data-recent-remove]');
    if (removeButton) {
      event.preventDefault();
      this.removeRecentSearch(removeButton.closest('.predictive-search__recent-item')?.dataset.term);
      return;
    }

    const popularChip = event.target.closest('[data-popular-term]');
    if (popularChip) {
      this.saveRecentSearch(popularChip.dataset.popularTerm || popularChip.textContent.trim());
      return;
    }

    const recentLink = event.target.closest('[data-recent-term]');
    if (recentLink) {
      this.saveRecentSearch(recentLink.closest('.predictive-search__recent-item')?.dataset.term);
      return;
    }

    const suggestion = event.target.closest('#predictive-search-results .predictive-search__item');
    if (suggestion && this.getQuery().length) {
      this.saveRecentSearch(this.getQuery());
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      this.openEmptyPanel();
      return;
    }

    this.closeEmptyPanel();
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    } else {
      this.saveRecentSearch(this.getQuery());
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.openEmptyPanel();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.openEmptyPanel();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
        this.closeEmptyPanel();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        this.closeEmptyPanel();
        break;
      default:
        if (!this.getQuery().length) this.openEmptyPanel();
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  /* ------- Etsy empty-state panel: recent + popular searches ------- */

  openEmptyPanel() {
    if (!this.emptyPanel || this.getQuery().length) return;
    this.renderRecentSearches();
    if (this.emptyPanel.querySelector('[data-popular-term], [data-recent-term]')) {
      this.setAttribute('empty-open', 'true');
    }
  }

  closeEmptyPanel() {
    if (this.emptyPanel) this.removeAttribute('empty-open');
  }

  buildSearchUrl(term) {
    const action = this.input.form.getAttribute('action') || '/search';
    return `${action}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
  }

  getRecentSearches() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(this.recentSearchesKey));
      return Array.isArray(stored) ? stored.filter((term) => typeof term === 'string' && term.length) : [];
    } catch {
      return [];
    }
  }

  setRecentSearches(terms) {
    try {
      window.localStorage.setItem(this.recentSearchesKey, JSON.stringify(terms.slice(0, this.maxRecentSearches)));
    } catch {
      // Storage unavailable (private mode, etc.) — recent searches are a progressive enhancement.
    }
  }

  saveRecentSearch(term) {
    const cleanTerm = (term || '').trim();
    if (!cleanTerm.length) return;
    const terms = this.getRecentSearches().filter(
      (existing) => existing.toLowerCase() !== cleanTerm.toLowerCase()
    );
    terms.unshift(cleanTerm);
    this.setRecentSearches(terms);
  }

  removeRecentSearch(term) {
    if (!term) return;
    this.setRecentSearches(this.getRecentSearches().filter((existing) => existing !== term));
    this.renderRecentSearches();
    if (!this.emptyPanel.querySelector('[data-popular-term], [data-recent-term]')) {
      this.closeEmptyPanel();
      this.input.focus();
    }
  }

  renderRecentSearches() {
    if (!this.recentWrapper || !this.recentList || !this.recentItemTemplate) return;
    const terms = this.getRecentSearches();
    this.recentList.innerHTML = '';
    terms.forEach((term) => {
      const item = this.recentItemTemplate.content.firstElementChild.cloneNode(true);
      item.dataset.term = term;
      item.querySelector('.predictive-search__recent-text').textContent = term;
      const link = item.querySelector('[data-recent-term]');
      link.setAttribute('href', this.buildSearchUrl(term));
      link.setAttribute('aria-label', `Search for ${term}`);
      this.recentList.appendChild(item);
    });
    this.recentWrapper.toggleAttribute('hidden', terms.length === 0);
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
